import type { IntlShape } from 'react-intl';
import type { AwardKind } from '@tg/shared';

/** tiers of these counters are multipliers in hundredths (×50 = 5000) */
export const X_KEYS: ReadonlySet<string> = new Set(['x', 'crash:x']);

/** ICU values for a tier threshold: `n` as is, `x` as a multiplier (achievements.big-win.desc "×{x}"). */
const tierValues = (n: number) => ({ n, x: n / 100 });

export function gameName(intl: IntlShape, game: string | null): string {
  if (!game) return '';
  const id = `common.game.${game}`;
  return id in intl.messages ? intl.formatMessage({ id }) : game;
}

export const achName = (intl: IntlShape, id: string, n: number): string =>
  intl.formatMessage({ id: `achievements.${id}.name` }, tierValues(n));

export const achDesc = (intl: IntlShape, id: string, n: number): string =>
  intl.formatMessage({ id: `achievements.${id}.desc` }, tierValues(n));

export interface AwardLike {
  kind: AwardKind;
  ach: string;
  value: number;
  game: string | null;
  title?: string | null;
}

/** Headline of an award: the achievement name, "×128.5 in Koi Pond" or "2nd place in “…”". Never a token amount. */
export function awardTitle(intl: IntlShape, a: AwardLike): string {
  if (a.kind === 'achievement') return achName(intl, a.ach, a.value);
  if (a.ach === 'place')
    return intl.formatMessage(
      { id: 'achievements.moment.place' },
      { place: a.value, title: a.title || intl.formatMessage({ id: 'share.card.place' }) },
    );
  return intl.formatMessage({ id: 'achievements.moment.win' }, { x: a.value / 100, game: gameName(intl, a.game) });
}

/** "Achievement" / "Moment" / "Tournament" */
export function awardKindLabel(intl: IntlShape, a: Pick<AwardLike, 'kind' | 'ach'>): string {
  const k = a.kind === 'achievement' ? 'achievement' : a.ach === 'place' ? 'place' : 'win';
  return intl.formatMessage({ id: `share.card.${k}` });
}
